import { prepareProfile, sortedPoints, evaluateResolvedProfile } from './light_profiles';

// Resolve the curve only. Source output limits, enables and overrides never
// become the follower's settings, and an inherited curve is never allocated.
export function resolveLightProfile(channel, channels, subzones, zoneSettings) {
  if (channel._profile_off) return { kind: 'off', source: null, points: [] };
  if (channel.points?.length) return { kind: 'custom', source: channel, points: channel.points };
  const subzone = subzones.find(
    (s) => (s.zone ?? channel.zone) === channel.zone && s.subzone === channel.subzone
  );
  const source = channels.find((candidate) =>
    zoneSettings?.shared_toplight
      ? candidate.zone === channel.zone && candidate.subzone === 0 && candidate.channel === 0
      : candidate.zone === channel.zone &&
        candidate.subzone === channel.subzone &&
        candidate.channel === subzone?.main_channel
  );
  return source && !source._profile_off
    ? { kind: source.points?.length ? 'custom' : 'analytic', source, points: source.points ?? [] }
    : { kind: 'off', source: null, points: [] };
}

// A normalized display curve, not a stored/custom profile. Each recipient uses
// its own minimum as the sunrise floor. Pass the effective maximum after masters.
export function analyticLightProfile(channel, cycle, sunriseDuration) {
  const maximum = channel.maximum ?? 100;
  if (!(maximum > 0)) return [];
  const duration = cycle.end - cycle.start;
  const ramp = Math.max(0, Number(sunriseDuration) || 0);
  const shoulder = Math.min(ramp, duration / 2);
  const floor = ((channel.minimum ?? 0) / maximum) * 100;
  return [...new Set([0, shoulder, duration - shoulder, duration])].map((offset) => ({
    anchor: 0,
    offset_minutes: offset,
    level:
      floor + (100 - floor) * (ramp ? Math.min(1, offset / ramp, (duration - offset) / ramp) : 1),
    interpolation: 0
  }));
}

export function evaluateAnalyticLightProfile(channel, cycle, sunriseDuration, minute) {
  const maximum = channel.maximum ?? 100;
  if (!(maximum > 0)) return 0;
  const elapsed = (((minute - cycle.start) % 1440) + 1440) % 1440;
  const duration = cycle.end - cycle.start;
  if (elapsed >= duration) return 0;
  const ramp = Math.max(0, Number(sunriseDuration) || 0);
  const floor = ((channel.minimum ?? 0) / maximum) * 100;
  return (
    floor + (100 - floor) * (ramp ? Math.min(1, elapsed / ramp, (duration - elapsed) / ramp) : 1)
  );
}

// Display the existing GrowBase/SAM simple program without creating a channel
// catalog. Legacy dimm_lights.c gives sunset priority when ramps overlap; it
// starts that ramp from the level already reached, unlike channel lighting.
// This is the nominal configured cycle, excluding thermal/manual intervention.
export function legacyLightOutputVertices({ cycle, minimum, maximum, sunriseDuration }) {
  const start = cycle.start - 60;
  const end = cycle.end + 60;
  const duration = cycle.end - cycle.start;
  if (!(maximum > 9) || minimum > maximum)
    return { start, end, vertices: [[start, 0], [end, 0]] };
  const ramp = Math.max(0, Number(sunriseDuration) || 0);
  const sunset = Math.max(0, duration - ramp);
  const peak = ramp && sunset > 0 ? minimum + (maximum - minimum) * Math.min(1, sunset / ramp) : maximum;
  const evaluate = (minute) => {
    const elapsed = ((minute - cycle.start) % 1440 + 1440) % 1440;
    if (elapsed >= duration) return 0;
    if (!ramp) return maximum;
    if (elapsed >= sunset) return minimum + (peak - minimum) * (duration - elapsed) / (duration - sunset);
    return minimum + (maximum - minimum) * Math.min(1, elapsed / ramp);
  };
  const offsets = [...new Set([0, Math.min(ramp, sunset), sunset, duration])];
  const moments = [...new Set([start, end, ...offsets.flatMap((offset) =>
    [-1, 0, 1].map((day) => cycle.start + offset + day * 1440)
      .filter((minute) => minute >= start && minute <= end)
  )])].sort((a, b) => a - b);
  const vertices = moments.flatMap((minute, index) => {
    const before = evaluate(minute - 0.000001);
    const after = evaluate(minute);
    return [...(index && Math.abs(before - after) > 0.001 ? [[minute, before]] : []), [minute, after]];
  });
  return { start, end, vertices };
}

// Configured automatic output for both individual and zone previews. This uses
// recipient maxima/masters/cutoff, including followers and shared top lights.
export function lightOutputVertices({
  cycle,
  channel,
  enabled,
  sunriseDuration,
  zoneMaster = 100,
  subzoneMaster = 100,
  profilePoints,
  range
}) {
  const custom = Boolean(profilePoints?.length);
  const resolved = custom ? prepareProfile(profilePoints, cycle).points : [];
  const validProfile = resolved.length > 0;
  const start = range?.start ?? Math.min(cycle.start - 60, ...resolved.map((p) => p.minute));
  const end = range?.end ?? Math.max(cycle.end + 60, ...resolved.map((p) => p.minute));
  const maximum = Number(channel?.maximum) || 0;
  const minimum = Math.max(0, Number(channel?.minimum) || 0);
  const factor =
    (Math.max(0, Math.min(100, Number(zoneMaster))) / 100) *
    (Math.max(0, Math.min(100, Number(subzoneMaster))) / 100);
  // SAM computes the analytic peak in tenths of a percent before applying the
  // sunrise fraction. Masters change its height, never its fixed cutoff floor.
  const analyticMaximum = Math.floor(maximum * factor * 10 + 0.0000001) / 10;
  const analyticChannel = { ...channel, minimum, maximum: analyticMaximum };
  let vertices = [
    [start, 0],
    [end, 0]
  ];
  if (
    channel &&
    enabled &&
    maximum > 0 &&
    (custom ? minimum <= maximum : analyticMaximum > 0 && minimum <= analyticMaximum) &&
    (custom ? validProfile : cycle.end > cycle.start)
  ) {
    const knots = custom
      ? resolved
      : sortedPoints(analyticLightProfile(analyticChannel, cycle, sunriseDuration), cycle);
    const evaluate = custom
      ? (minute) => (evaluateResolvedProfile(resolved, minute) * maximum) / 100
      : (minute) =>
          (evaluateAnalyticLightProfile(analyticChannel, cycle, sunriseDuration, minute) *
            analyticMaximum) /
          100;
    const moments = [
      ...new Set([
        start,
        end,
        ...knots.flatMap((point) => {
          const first = Math.ceil((start - point.minute) / 1440);
          const last = Math.floor((end - point.minute) / 1440);
          return Array.from(
            { length: Math.max(0, last - first + 1) },
            (_, day) => point.minute + (first + day) * 1440
          );
        })
      ])
    ].sort((a, b) => a - b);
    // Sample both sides only for real jumps. Analytic and custom curves repeat
    // daily, including padding and 24-hour operation without an invented night.
    vertices = moments.flatMap((minute, index) => {
      const after = evaluate(minute);
      const before = evaluate(minute - 0.000001);
      return [
        ...(index && Math.abs(before - after) > 0.001 ? [[minute, before]] : []),
        [minute, after]
      ];
    });
  }
  if (custom) {
    const scaled = vertices.map(([minute, level]) => [minute, level * factor]);
    const output = [];
    scaled.forEach(([minute, level], index) => {
      const previous = scaled[index - 1];
      if (
        previous &&
        minimum > 0 &&
        ((previous[1] < minimum && level >= minimum) || (previous[1] >= minimum && level < minimum))
      ) {
        const crossing =
          previous[0] + ((minimum - previous[1]) / (level - previous[1])) * (minute - previous[0]);
        output.push(
          [crossing, previous[1] < minimum ? 0 : minimum],
          [crossing, level < minimum ? 0 : minimum]
        );
      }
      output.push([minute, level < minimum ? 0 : level]);
    });
    vertices = output.filter(
      (point, index) =>
        !index || point[0] !== output[index - 1][0] || point[1] !== output[index - 1][1]
    );
  }
  return { vertices, start, end };
}
